import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { LayoutGrid } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  StatusPill,
  TooltipProvider,
} from '@/components/ui';
import { useInfiniteDesigns, designKeys } from '@/hooks/useDesigns';
import { usePublishChannels } from '@/hooks/usePublishChannels';
import { useQueryClient } from '@tanstack/react-query';
import type { Design } from '@/api/designs';
import { formatDate } from '@/lib/format-date';
import { formatMoney } from '@/lib/format-money';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { cn } from '@/lib/utils';
import { isSendableChannel } from '@/lib/sendable-channel';
import { ChannelStatusCell } from './ChannelStatusCell';
import { SendToChannelBar } from './SendToChannelBar';
import { DesignRowActions } from './DesignRowActions';
import { useDesignEntry } from './DesignEntryChoice';
import { useNotifyShopOnPublish } from './useNotifyShopOnPublish';

export interface DesignsTableProps {
  className?: string;
}

/** Status column of a row; derived from channel state, not from Design.status. */
function statusVariant(design: Design) {
  switch (design.derivedStatus) {
    case 'published':
      return 'published' as const;
    case 'completed':
      return 'neutral' as const;
    default:
      return 'draft' as const;
  }
}

/**
 * Why a listing cannot be selected. The seller fixes all of these in the
 * creator, so the row stays visible and says what is missing.
 */
function blockedReason(design: Design): string | null {
  // Decided by the server: the list carries a colour count, not the colour
  // rows, and the dispatch endpoint applies the same rule again.
  switch (design.sendBlockReason) {
    case 'missing_title':
      return 'missingTitle';
    case 'no_active_variant':
      return 'noVariant';
    case 'unpriced_product':
      return 'unpricedProduct';
    case 'negative_profit':
      return 'negativeProfit';
    case 'product_uncalibrated':
      return 'productUncalibrated';
    case 'position_uncalibrated':
      return 'positionUncalibrated';
    default:
      return null;
  }
}

/** The text of a blocked row; names product, position and minimum price where known. */
function blockedText(
  design: Design,
  blocked: string,
  t: TFunction,
  language: string
): string {
  const detail = design.sendBlockDetail;
  if (
    blocked === 'negativeProfit' &&
    detail &&
    typeof detail.minimumPrice === 'number'
  ) {
    return t('seller.designs.table.blocked.negativeProfitDetail', {
      productName: detail.productName,
      minimumPrice: formatMoney(detail.minimumPrice, 'EUR', language),
    });
  }
  if (blocked === 'productUncalibrated' && detail) {
    return t('seller.designs.table.blocked.productUncalibratedDetail', {
      productName: detail.productName,
    });
  }
  if (blocked === 'positionUncalibrated' && detail?.positionName) {
    return t('seller.designs.table.blocked.positionUncalibratedDetail', {
      productName: detail.productName,
      positionName: detail.positionName,
    });
  }
  return t(`seller.designs.table.blocked.${blocked}`);
}

/**
 * The seller overview as a table (plan U10).
 *
 * One row per listing with one column per publish channel, so the seller sees
 * at a glance where a listing is and where it failed. Rows carry a checkbox
 * for the bulk send; rows that cannot be sent keep their checkbox disabled and
 * say why rather than disappearing.
 */
export function DesignsTable({ className }: DesignsTableProps) {
  const { t, i18n } = useTranslation();
  const { startClassic } = useDesignEntry();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const {
    data,
    isPending,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteDesigns();

  const channelResolution = usePublishChannels();
  const designs = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );
  const total = data?.pages[0]?.meta.total ?? 0;

  // Inactive channels stay as a column while they still carry state, so a
  // listing does not silently lose the record of where it went. Memoised so
  // the marketplace-id list below does not change identity on every render.
  const columns = useMemo(
    () => channelResolution.data?.channels ?? [],
    [channelResolution.data]
  );

  const selectableIds = useMemo(
    () => designs.filter((design) => !blockedReason(design)).map((d) => d.id),
    [designs]
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleRow = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  // The shop hears about new products from here now; the publish step has no
  // synchronous result to report once the send became a job.
  const marketplaceChannelIds = useMemo(
    () =>
      columns
        .filter((channel) => channel.type === 'MARKETPLACE')
        .map((channel) => channel.id),
    [columns]
  );
  useNotifyShopOnPublish(designs, marketplaceChannelIds);

  const afterSend = () => {
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: designKeys.lists() });
  };

  return (
    <section
      aria-labelledby="designs-list-title"
      className={cn('flex flex-col', className)}
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <p className="dc-eyebrow">{t('seller.designs.list.eyebrow')}</p>
          <h2 id="designs-list-title" className="dc-h2 text-xl text-ink">
            {t('seller.designs.list.title')}
            {data && (
              <span className="ml-2 font-mono text-[12.5px] font-medium tracking-normal text-mute">
                {t('seller.designs.list.count', { count: total })}
              </span>
            )}
          </h2>
        </div>
        <p className="text-[12.5px] text-mute">
          {t('seller.designs.list.sortHint')}
        </p>
      </div>

      {selected.size > 0 && columns.length > 0 && (
        <div className="mb-4">
          <SendToChannelBar
            selectedDesignIds={[...selected]}
            channels={columns.filter(isSendableChannel)}
            onSent={afterSend}
          />
        </div>
      )}

      {isPending ? (
        <LoadingState label={t('seller.designs.list.loading')} />
      ) : isError && !data ? (
        <Card className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-danger">
            {t('seller.designs.list.loadFailed')}
          </p>
          <Button variant="outline" onClick={() => void refetch()}>
            {t('seller.designs.list.retry')}
          </Button>
        </Card>
      ) : designs.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid className="h-8 w-8" />}
          title={t('seller.designs.list.empty.title')}
          description={t('seller.designs.list.empty.text')}
          action={
            <Button variant="primary" onClick={startClassic}>
              {t('seller.designs.list.empty.action')}
            </Button>
          }
        />
      ) : (
        <>
          {/* Only the channel columns scroll; preview and name stay put, so a
              seller with five stores can still tell the rows apart. The
              provider is local: the seller shell has none, unlike the creator
              shell, and a failure cell without one crashes the page. */}
          <TooltipProvider delayDuration={300}>
            <div className="w-full overflow-x-auto rounded-card bg-paper-2 ring-1 ring-line">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="sticky left-0 z-10 bg-paper-2 p-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={selectableIds.length === 0}
                        aria-label={t('seller.designs.table.selectAll')}
                      />
                    </th>
                    <th className="sticky left-12 z-10 bg-paper-2 p-3 text-left dc-eyebrow">
                      {t('seller.designs.table.listing')}
                    </th>
                    <th className="p-3 text-left dc-eyebrow">
                      {t('seller.designs.table.status')}
                    </th>
                    {columns.map((channel) => (
                      <th
                        key={channel.id}
                        className="p-3 text-center dc-eyebrow whitespace-nowrap"
                      >
                        {channel.displayName}
                        {!channel.isActive && (
                          <span className="ml-1 text-mute">
                            {t('seller.designs.table.channelRemoved')}
                          </span>
                        )}
                      </th>
                    ))}
                    <th className="p-3 text-left dc-eyebrow">
                      {t('seller.designs.table.updated')}
                    </th>
                    <th className="p-3 text-right dc-eyebrow">
                      {t('seller.designs.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {designs.map((design) => {
                    const blocked = blockedReason(design);
                    const byChannel = new Map(
                      (design.channelPublications ?? []).map((publication) => [
                        publication.channelId,
                        publication,
                      ])
                    );

                    return (
                      <tr
                        key={design.id}
                        className="border-b border-line last:border-0 hover:bg-paper"
                      >
                        <td className="sticky left-0 z-10 bg-paper-2 p-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={selected.has(design.id)}
                            onChange={() => toggleRow(design.id)}
                            disabled={blocked !== null}
                            aria-label={t('seller.designs.table.selectRow', {
                              name: design.name,
                            })}
                            title={
                              blocked
                                ? blockedText(design, blocked, t, i18n.language)
                                : undefined
                            }
                          />
                        </td>
                        <td className="sticky left-12 z-10 bg-paper-2 p-3">
                          <div className="flex items-center gap-3">
                            {(design.coverImageUrl || design.thumbnailUrl) && (
                              <img
                                src={
                                  proxyStorageUrl(
                                    design.coverImageUrl || design.thumbnailUrl
                                  ) ?? ''
                                }
                                alt=""
                                className="h-10 w-10 rounded-control object-cover"
                              />
                            )}
                            <span className="font-medium text-ink">
                              {design.name}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col items-start gap-1">
                            <StatusPill variant={statusVariant(design)}>
                              {t(
                                `seller.designs.derivedStatus.${design.derivedStatus ?? 'draft'}`
                              )}
                            </StatusPill>
                            {design.hasUnpublishedChanges && (
                              <StatusPill variant="warning">
                                {t('seller.designs.unpublishedChanges')}
                              </StatusPill>
                            )}
                          </div>
                        </td>
                        {columns.map((channel) => (
                          <td key={channel.id} className="p-3 text-center">
                            <ChannelStatusCell
                              publication={byChannel.get(channel.id)}
                            />
                          </td>
                        ))}
                        <td className="p-3 text-mute whitespace-nowrap">
                          {formatDate(design.updatedAt, i18n.language)}
                        </td>
                        <td className="p-3 text-right">
                          <DesignRowActions design={design} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TooltipProvider>

          {hasNextPage && (
            <div className="mt-7 flex flex-col items-center gap-3">
              {isFetchNextPageError && (
                <p role="alert" className="text-sm text-danger">
                  {t('seller.designs.list.loadMoreError')}
                </p>
              )}
              <Button
                variant="outline"
                loading={isFetchingNextPage}
                loadingLabel={t('seller.designs.list.loadingMore')}
                onClick={() => void fetchNextPage()}
              >
                {isFetchNextPageError
                  ? t('seller.designs.list.retry')
                  : t('seller.designs.list.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
